/* eslint-disable new-cap */
/**
 * Usage API hooks for tenant users
 * Calls Python API endpoints at /api/usage/*
 */

import restClient from "lib/rest-client";
import { useCallback, useEffect, useRef, useState } from "react";

// =====================================
// Types
// =====================================

/** カテゴリ別利用状況 */
export type CategoryUsage = {
  category: string;
  label: string;
  used: number;
  limit: number;
  remaining: number;
  usageRate: number;
  isWarning: boolean;
  unit: string;
};

/** 契約概要 */
export type ContractSummary = {
  plan: string;
  status: string;
  periodStart: string;
  periodEnd: string;
};

/** 利用状況サマリー */
export type UsageSummaryResponse = {
  tenantId: string;
  contract: ContractSummary;
  categories: CategoryUsage[];
};

/** 期間別利用状況 */
export type PeriodUsage = {
  periodStart: string;
  periodEnd: string;
  categories: CategoryUsage[];
};

/** 利用履歴レスポンス */
export type UsageHistoryResponse = {
  periods: PeriodUsage[];
};

// =====================================
// useUsageSummary
// =====================================

/** useUsageSummaryの戻り値 */
export type UseUsageSummaryResult = {
  data: UsageSummaryResponse | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<UsageSummaryResponse>;
};

/**
 * 現在の利用状況サマリーを取得するhook
 * @return {UseUsageSummaryResult} 利用状況サマリー取得結果
 */
export function useUsageSummary(): UseUsageSummaryResult {
  const [data, setData] = useState<UsageSummaryResponse | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (): Promise<UsageSummaryResponse> => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await restClient.get<UsageSummaryResponse>("/usage");
      if (mountedRef.current) {
        setData(result);
      }
      return result;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (mountedRef.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useUsageHistory
// =====================================

/** useUsageHistoryの戻り値 */
export type UseUsageHistoryResult = {
  data: UsageHistoryResponse | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<UsageHistoryResponse>;
};

/**
 * 利用履歴を取得するhook
 * @param {number} periods 取得する期間数
 * @return {UseUsageHistoryResult} 利用履歴取得結果
 */
export function useUsageHistory(periods = 6): UseUsageHistoryResult {
  const [data, setData] = useState<UsageHistoryResponse | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (): Promise<UsageHistoryResponse> => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await restClient.get<UsageHistoryResponse>(
        "/usage/history",
        { periods: String(periods) },
      );
      if (mountedRef.current) {
        setData(result);
      }
      return result;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (mountedRef.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [periods]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
